import React, { useRef, useState } from "react";
import {
  Box,
  Typography,
  Divider,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Paper,
} from "@mui/material";
import Grid from "@mui/material/Grid2";
import { useStyles } from "../../assets/styles/Theme";
import GrnPdfHeader from "../../components/documents/GrnPdfHeader";
import GrnPdfFooter from "../../components/documents/GrnPdfFooter";
import ViewDataGridPdf from "../../components/grid/ViewDataGridPdf";
import { ViewReturnNotePdfData } from "../../components/grid/GridDataPdf";
import GrnPdfNotes from "../../components/documents/GrnPdfNotes";
import GrnPdfSupplier from "../../components/documents/GrnPdfSupplier";
import GrnPdfInfo from "../../components/documents/GrnPdfInfo";
import { FormatDate } from "../../utils/common/DateUtils";
import { useTheme, ThemeProvider } from "@mui/material/styles";
import { lightTheme } from "../../assets/styles/Theme";
import { FormatDateYYYYMM } from "../../utils/common/DateUtils";
import { GenerateNoteNumber } from "../../utils/common/SupportingFunctions";
import { useSelector } from "react-redux";
import { CON_NOTE_DEBIT, CON_NOTE_DELIVERY } from "../../utils/sys/Constants";

const cellStyleLabel = {
  border: "1px solid black",
  fontWeight: "bold",
  fontSize: 12,
  textAlign: "right",
  px: 1,
  py: 0.5,
  bgcolor: "white",
  color: "black",
};

const cellStyleValue = {
  border: "1px solid black",
  fontSize: 12,
  px: 1,
  py: 0.5,
  textAlign: "right",
  bgcolor: "white",
  color: "black",
};

const parseNumber = (value) => {
  if (value === null || value === undefined || value === "") return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

function DebitNotePdf({
  docType,
  grnHeader,
  selectedSupplier,
  grnDetailsForm,
  grnPaySummery,
  grnData,
  grnItems,
  grnCompany,
  grnReturns,
  returnedProducts,
  Returncal,
  grnReturnList,
  grnReturnMaster,
  grnreturncode,
  debitnoteno,
} = {}) {
  const theme = useTheme();
  const styles = useStyles(theme);
  const printRef = useRef();

  const handlePrint = () => window.print();

  const summary = grnPaySummery?.[0] || {};

  const formatCurrency = (val) =>
    new Intl.NumberFormat("en-LK", { minimumFractionDigits: 2 }).format(Number(val || 0));

  // ─── Helper: filter any array by grnreturncode ───────────────────────────
  const filterByReturnCode = (arr) => {
    if (!grnreturncode || !Array.isArray(arr)) return arr ?? [];
    return arr.filter(
      (item) => String(item.grnreturncode) === String(grnreturncode)
    );
  };
console.log("det", debitnoteno,);
  // ─── Filter all data sources upfront ─────────────────────────────────────
  const filteredGrnReturns      = filterByReturnCode(grnReturns);
  const filteredGrnReturnList   = filterByReturnCode(grnReturnList);
  const filteredGrnReturnMaster = filterByReturnCode(grnReturnMaster);
  const filteredReturncal       = filterByReturnCode(Returncal);

  // ─── localStorage also filtered ──────────────────────────────────────────
  const localReturned = (() => {
    try {
      const parsed = JSON.parse(localStorage.getItem("returnedProducts") || "[]");
      return filterByReturnCode(parsed);
    } catch {
      return [];
    }
  })();

  returnedProducts = (Array.isArray(returnedProducts) && returnedProducts.length)
    ? filterByReturnCode(returnedProducts)
    : localReturned;

  grnReturns = filteredGrnReturns.length
    ? filteredGrnReturns
    : returnedProducts;

  // ─── API return items (filtered) ─────────────────────────────────────────
  const apiReturnItems =
    filteredGrnReturnList.length > 0
      ? filteredGrnReturnList
      : filteredReturncal.length > 0
      ? filteredReturncal[0]?.grnReturnItems || filteredReturncal[0]?.returnList || []
      : [];

  // ─── Latest Returncal entry (filtered) ───────────────────────────────────
  const latestReturncalEntry =
    filteredReturncal.length > 0
      ? filteredReturncal[filteredReturncal.length - 1]
      : null;

  // ─── Latest return master (filtered) ─────────────────────────────────────
  const getLatestReturnMaster = () => {
    const source =
      filteredGrnReturnMaster.length > 0
        ? filteredGrnReturnMaster
        : filteredGrnReturnList;

    if (!source.length) return null;

    return [...source].sort(
      (a, b) => new Date(b.endate || 0) - new Date(a.endate || 0)
    )[0];
  };

  const latestReturnMaster = getLatestReturnMaster();

  // ─── Summary entries ──────────────────────────────────────────────────────
  const summarySet =
    Array.isArray(latestReturncalEntry?.grnReturnSummaries) &&
    latestReturncalEntry.grnReturnSummaries.length > 0
      ? latestReturncalEntry.grnReturnSummaries
      : [];
  const summaryEntry = summarySet.length > 0 ? summarySet[summarySet.length - 1] : null;

  const masterSummarySet =
    Array.isArray(latestReturnMaster?.grnReturnSummaries) &&
    latestReturnMaster.grnReturnSummaries.length > 0
      ? latestReturnMaster.grnReturnSummaries
      : [];
  const masterSummaryEntry =
    masterSummarySet.length > 0 ? masterSummarySet[masterSummarySet.length - 1] : null;

  const apiReturnSummary = summaryEntry || masterSummaryEntry || {};

  // ─── Build table rows ─────────────────────────────────────────────────────
  const grnItemsRows = grnReturns
    ?.map((item, index) => {
      const matchedReturned = returnedProducts.find(
        (retItem) => retItem.supproductcode === item.supproductcode
      );

      const matchedItem = grnItems?.find(
        (grnItem) =>
          grnItem.productcode === item.productcode ||
          grnItem.supproductcode === item.supproductcode
      );

      // Filter apiReturnItems by grnreturncode first, then match by supproductcode
      const apiMatches = apiReturnItems.filter(
        (apiItem) => Number(apiItem.supproductcode) === Number(item.supproductcode)
      );
      const matchedApiItem =
        apiMatches.length === 0
          ? undefined
          : (latestReturnMaster?.grnreturncode != null
              ? apiMatches.find(
                  (m) => String(m?.grnreturncode) === String(latestReturnMaster.grnreturncode)
                )
              : null) ?? apiMatches[apiMatches.length - 1];

      const productcode     = item?.productcode     || matchedItem?.productcode     || "";
      const immeasuringunit = item?.immeasuringunit  || matchedItem?.immeasuringunit  || "";
      const productname     = item?.productname     || matchedItem?.productname     || "";

const unitprice =
  (Number(matchedItem?.perunittaxableamount) || 0) +
  (Number(matchedItem?.perunitchargevalue) || 0);

      const receivedqty = item?.receivedqty || matchedItem?.acceptedqty || 0;
      const returnedqty =
        matchedApiItem?.returnedqty ?? item?.returnedqty ?? matchedReturned?.returnedqty ?? 0;
      const taxrate = item?.taxrate || matchedItem?.taxrate || 0;

      const unitcostwithouttax =
        matchedApiItem?.unitcostwithouttax != null
          ? Number(matchedApiItem.unitcostwithouttax)
          : item?.perunittaxableamount || matchedItem?.perunittaxableamount || unitprice || 0;

      const taxvalue =
        matchedApiItem?.taxperunit != null
          ? Number(matchedApiItem.taxperunit)
          : (taxrate * unitprice) / 100 || 0;

      const taxAmount =
        parseNumber(matchedApiItem?.taxamount) ??
        parseNumber(item?.taxamount) ??
        taxvalue * returnedqty;

     const total_price = unitprice * returnedqty;
      const totalprice =
        total_price;

      const reason        = matchedReturned?.reason   || item?.reason   || item?.descrip || "";
      const remarks       = matchedReturned?.remarks  || item?.remarks  || "";
      const attachmentName =
        matchedReturned?.dtoDOCs?.[0]?.filename ||
        matchedReturned?.dtoDOCs?.[0]?.FILENAME ||
        "";

      return {
        ...item,
        idno: index + 1,
        productcode,
        productname,
        immeasuringunit,
        unitprice,
        receivedqty,
        returnedqty,
        taxrate,
        taxvalue,
        taxAmount,
        total_price,
        totalprice,
        totalAmount: taxAmount,
        reason,
        remarks,
        attachmentName,
        id: productcode || index,
      };
    })
    .filter((item) => item.returnedqty > 0)
    .filter(
      (item, idx, arr) =>
        arr.findIndex(
          (r) =>
            String(r?.supproductcode ?? r?.productcode ?? "") ===
            String(item?.supproductcode ?? item?.productcode ?? "")
        ) === idx
    )
    .map((item, index) => ({ ...item, idno: index + 1 }));

  const grnItemsColumns = [
    { field: "idno",            headerName: "No" },
    { field: "productcode",     headerName: "Item Code" },
    { field: "productname",     headerName: "Item Name" },
    { field: "immeasuringunit", headerName: "UOM" },
    { field: "receivedqty",     headerName: "Total Quantity" },
    { field: "returnedqty",     headerName: "Returned Quantity" },
    { field: "unitprice",       headerName: "Unit Cost",           format: formatCurrency },
    { field: "taxrate",         headerName: "Tax Rate(%)",         format: (v) => (v ? `${v}%` : "0%") },
    { field: "taxvalue",        headerName: "Tax Amount Per Unit", format: formatCurrency },
    { field: "taxAmount",       headerName: "Tax Amount",          format: formatCurrency },
    { field: "totalprice",      headerName: "Total Price",         format: formatCurrency },
  ];

  // ─── Footer totals ────────────────────────────────────────────────────────
  const rowTotalBeforeTax = grnItemsRows.reduce((sum, item) => sum + (Number(item.total_price) || 0), 0);
  const rowTotalTax       = grnItemsRows.reduce((sum, item) => sum + (Number(item.taxAmount)   || 0), 0);
  const rowGrandTotal     = rowTotalBeforeTax + rowTotalTax;

  const totalbeforetax =
    apiReturnSummary?.totalbeforetax != null && apiReturnSummary?.totalbeforetax !== ""
      ? Number(apiReturnSummary.totalbeforetax)
      : latestReturnMaster?.totalbeforetax != null && latestReturnMaster?.totalbeforetax !== ""
      ? Number(latestReturnMaster.totalbeforetax)
      : latestReturnMaster?.tottaxableamount != null && latestReturnMaster?.tottaxableamount !== ""
      ? Number(latestReturnMaster.tottaxableamount)
      : rowTotalBeforeTax;

  const totaltax =
    latestReturnMaster?.tottaxamount != null && latestReturnMaster?.tottaxamount !== ""
      ? Number(latestReturnMaster.tottaxamount)
      : apiReturnSummary?.tottaxamount != null
      ? Number(apiReturnSummary.tottaxamount)
      : rowTotalTax;

  const grandtotal =
    latestReturnMaster?.totaftertaxamount != null && latestReturnMaster?.totaftertaxamount !== ""
      ? Number(latestReturnMaster.totaftertaxamount)
      : apiReturnSummary?.totaftertaxamount != null
      ? Number(apiReturnSummary.totaftertaxamount)
      : apiReturnSummary?.totreturnedvalue != null
      ? Number(apiReturnSummary.totreturnedvalue)
      : rowGrandTotal;

  // ─── Header / info fields ─────────────────────────────────────────────────
  const poData = grnHeader || grnDetailsForm || {};

  const poNumber =
   poData.ponumber  || "-";
  const poDate         = poData.podate      || poData.poDate      || null;
  const invoiceNumber  = poData.invoiceno   || poData.invoiceNumber || "N/A";
  const invoiceDate    = poData.invoicedate || poData.invoiceDate  || null;
  const deliveryDate   = poData.deliverynotedate || null;

  const companyName = grnCompany?.compname     || "N/A";
  const address1    = grnCompany?.compaddresS1 || "";
  const address2    = grnCompany?.compaddresS2 || "";
  const address3    = grnCompany?.compaddresS3 || "";
  const city        = grnCompany?.city         || "";
  const province    = grnCompany?.province     || "";
  const country     = grnCompany?.country      || "";
  const fullAddress = [address1, address2, address3, city, province, country].filter(Boolean).join(", ");
  const contact     = grnCompany?.hotline      || "N/A";
  const email       = grnCompany?.email        || "N/A";

  return (
    <div ref={printRef} id="print-section">
      <ThemeProvider theme={lightTheme}>
        <Box sx={{ maxWidth: 794, mx: "auto", p: 3, bgcolor: "white" }}>
          <GrnPdfHeader
            docType={docType}
            grnHeader={grnHeader}
            grnCompany={grnCompany}
            handlePrint={handlePrint}
          />

          <Divider sx={{ borderColor: "#2a6db0", mb: 4 }} />

          <Box mb={4} sx={{ color: "black" }}>
            <Typography variant="body_xs" component="span" fontWeight="bold">DEBIT NOTE NO:</Typography>{" "}
           <Typography variant="body_xs" component="span">
  {debitnoteno ||
    GenerateNoteNumber(
      CON_NOTE_DEBIT,
      FormatDateYYYYMM(invoiceDate),
      poData?.grncode
    )}
</Typography>
<br />

            <Typography variant="body_xs" component="span" fontWeight="bold">DEBIT NOTE DATE:</Typography>{" "}
            <Typography variant="body_xs" component="span">{FormatDate(invoiceDate) || "-"}</Typography><br />

            <Typography variant="body_xs" component="span" fontWeight="bold">INVOICE NUMBER:</Typography>{" "}
            <Typography variant="body_xs" component="span">{invoiceNumber}</Typography><br />

            <Typography variant="body_xs" component="span" fontWeight="bold">INVOICE DATE:</Typography>{" "}
            <Typography variant="body_xs" component="span">{FormatDate(invoiceDate) || "-"}</Typography><br />

            <Typography variant="body_xs" component="span" fontWeight="bold">GRN ID:</Typography>{" "}
            <Typography variant="body_xs" component="span">{grnHeader?.grncode || "N/A"}</Typography><br />

            <Typography variant="body_xs" component="span" fontWeight="bold">GRN DATE:</Typography>{" "}
            <Typography variant="body_xs" component="span">{FormatDate(poDate) || "-"}</Typography><br />

            <Typography variant="body_xs" component="span" fontWeight="bold">PO NUMBER:</Typography>{" "}
            <Typography variant="body_xs" component="span">{poNumber}</Typography><br />

            <Typography variant="body_xs" component="span" fontWeight="bold">PO DATE:</Typography>{" "}
            <Typography variant="body_xs" component="span">{FormatDate(poDate) || "-"}</Typography><br />

            <Typography variant="body_xs" component="span" fontWeight="bold">DELIVERY NOTE NO:</Typography>{" "}
            <Typography variant="body_xs" component="span">
            {poData?.deliverynoteno || "-"}
            </Typography><br />

            <Typography variant="body_xs" component="span" fontWeight="bold">DELIVERY DATE:</Typography>{" "}
            <Typography variant="body_xs" component="span">{FormatDate(deliveryDate) || "-"}</Typography>
          </Box>

          <Box sx={styles.sup_buy_container}>
            <Box sx={styles.sup_buy_container_inside}>
              <GrnPdfSupplier grnHeader={grnHeader} selectedSupplier={selectedSupplier} />
            </Box>
          </Box>

          <ViewDataGridPdf
            columns={grnItemsColumns}
            rows={grnItemsRows}
            gridid="productcode"
            sx={styles.view_data_grid_page}
          />

          <Box display="flex" justifyContent="flex-end" mt={2} maxWidth={300} ml="auto">
            <TableContainer component={Paper} sx={{ border: "1px solid black" }}>
              <Table size="small" sx={{ borderCollapse: "collapse" }}>
                <TableBody>
                  <TableRow>
                    <TableCell sx={cellStyleLabel}>TOTAL BEFORE TAX</TableCell>
                    <TableCell sx={cellStyleValue}>{formatCurrency(totalbeforetax)}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell sx={cellStyleLabel}>TOTAL TAX</TableCell>
                    <TableCell sx={cellStyleValue}>{formatCurrency(totaltax)}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell sx={cellStyleLabel}>TOTAL</TableCell>
                    <TableCell sx={cellStyleValue}>{formatCurrency(grandtotal)}</TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          </Box>

          <GrnPdfInfo grnHeader={grnHeader} />
          <GrnPdfFooter />
        </Box>
      </ThemeProvider>
    </div>
  );
}

export default DebitNotePdf;